import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";

import * as teamService from "../services/teamService";
import { inviteCompanyMember } from "../services/inviteService";
import { getCompanyUsers } from "../services/companyService";

/* ===================================================== */
/* TEAM DETAILS PAGE                                     */
/* ===================================================== */

export default function TeamDetailsPage() {
  const { id } = useParams();

  /* ================= STATE ================= */

  const [team, setTeam] = useState(null);
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [addingId, setAddingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteMsg, setInviteMsg] = useState("");
  const [inviteError, setInviteError] = useState("");

  /* ===================================================== */
  /* LOAD DATA                                             */
  /* ===================================================== */

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [teamData, usersData] = await Promise.all([
        teamService.getTeam(id),
        getCompanyUsers(),
      ]);

      setTeam(teamData);
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (e) {
      setError(e.message || "Failed to load team");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) loadData();
  }, [id, loadData]);

  /* ===================================================== */
  /* DERIVED DATA                                          */
  /* ===================================================== */

  const memberIds = useMemo(() => {
    return (team?.members || []).map((m) =>
      typeof m === "string" ? m : m._id
    );
  }, [team]);

  const availableUsers = useMemo(() => {
    return users.filter((u) => !memberIds.includes(String(u._id)));
  }, [users, memberIds]);

  /* ===================================================== */
  /* ACTIONS                                               */
  /* ===================================================== */

  /* ---------- Add member (optimistic) ---------- */
  const handleAdd = async (user) => {
    const userId = user._id;

    setAddingId(userId);

    try {
      await teamService.addMemberToTeam(id, userId);

      setTeam((prev) => ({
        ...prev,
        members: [...(prev.members || []), user],
      }));
    } catch (e) {
      alert(e.message || "Failed to add member");
    } finally {
      setAddingId(null);
    }
  };

  /* ---------- Remove member (optimistic) ---------- */
  const handleRemove = async (userId) => {
    if (!window.confirm("Remove this member?")) return;

    setRemovingId(userId);

    const snapshot = team.members;

    setTeam((prev) => ({
      ...prev,
      members: prev.members.filter(
        (m) => String(m._id || m) !== String(userId)
      ),
    }));

    try {
      await teamService.removeMemberFromTeam(id, userId);
    } catch (e) {
      alert(e.message || "Failed to remove member");

      // rollback
      setTeam((prev) => ({
        ...prev,
        members: snapshot,
      }));
    } finally {
      setRemovingId(null);
    }
  };

  /* ---------- Invite ---------- */
  const handleInvite = async ({ email, name }) => {
    setInviteBusy(true);
    setInviteError("");
    setInviteMsg("");

    try {
      await inviteCompanyMember({
        email,
        name,
        teamId: id,
      });

      setInviteMsg("Invite sent successfully");

      await loadData(); // safe refresh
    } catch (e) {
      setInviteError(e.message || "Failed to invite");
    } finally {
      setInviteBusy(false);
    }
  };

  /* ===================================================== */
  /* UI STATES                                             */
  /* ===================================================== */

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading team…</div>;
  }

  if (error) {
    return (
      <div className="p-6 space-y-3">
        <div className="text-rose-600">{error}</div>
        <button
          onClick={loadData}
          className="text-indigo-600 text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!team) {
    return <div className="p-6">Team not found</div>;
  }

  /* ===================================================== */
  /* RENDER                                                */
  /* ===================================================== */

  return (
    <div className="mx-auto max-w-7xl p-6">

      {/* HEADER */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">{team.name}</h1>
        {team.description && (
          <p className="text-sm text-gray-500">
            {team.description}
          </p>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">

        {/* MEMBERS */}
        <div className="lg:col-span-2">
          <div className="rounded-lg border bg-white p-4">
            <h2 className="font-medium mb-3">
              Members ({memberIds.length})
            </h2>

            {memberIds.length === 0 ? (
              <div className="text-sm text-gray-500">
                No members yet
              </div>
            ) : (
              <ul className="divide-y">
                {team.members.map((m) => {
                  const user =
                    typeof m === "string"
                      ? { _id: m, name: "User" }
                      : m;

                  const idStr = String(user._id);

                  return (
                    <li
                      key={idStr}
                      className="py-3 flex justify-between items-center"
                    >
                      <div>
                        <div className="font-medium text-sm">
                          {user.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {user.email}
                        </div>
                      </div>

                      <button
                        onClick={() => handleRemove(idStr)}
                        disabled={removingId === idStr}
                        className="text-sm text-rose-600"
                      >
                        {removingId === idStr
                          ? "Removing…"
                          : "Remove"}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* SIDE PANEL */}
        <div className="space-y-4">

          {/* ADD EXISTING */}
          <div className="rounded-lg border bg-white p-4">
            <h3 className="font-medium mb-2">
              Add existing user
            </h3>

            {availableUsers.length === 0 ? (
              <div className="text-sm text-gray-500">
                No users available
              </div>
            ) : (
              <ul className="space-y-2">
                {availableUsers.map((u) => (
                  <li
                    key={u._id}
                    className="flex justify-between items-center"
                  >
                    <span className="text-sm">
                      {u.name}
                    </span>

                    <button
                      onClick={() => handleAdd(u)}
                      disabled={addingId === u._id}
                      className="text-xs bg-gray-900 text-white px-2 py-1 rounded"
                    >
                      {addingId === u._id
                        ? "Adding…"
                        : "Add"}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* INVITE */}
          <InviteCard
            onInvite={handleInvite}
            busy={inviteBusy}
            msg={inviteMsg}
            error={inviteError}
          />

        </div>
      </div>
    </div>
  );
}

/* ===================================================== */
/* INVITE CARD                                           */
/* ===================================================== */

function InviteCard({ onInvite, busy, msg, error }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  const submit = () => {
    if (!email.trim()) return alert("Email required");

    onInvite({
      email: email.trim(),
      name: name.trim() || undefined,
    });

    setEmail("");
    setName("");
  };

  return (
    <div className="rounded-lg border bg-white p-4">
      <h3 className="font-medium mb-2">Invite user</h3>

      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name"
        className="w-full border rounded px-3 py-2 mb-2"
      />

      <input
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email"
        className="w-full border rounded px-3 py-2 mb-3"
      />

      <button
        onClick={submit}
        disabled={busy}
        className="w-full bg-gray-900 text-white py-2 rounded"
      >
        {busy ? "Inviting…" : "Invite"}
      </button>

      {msg && (
        <div className="mt-2 text-sm text-green-600">
          {msg}
        </div>
      )}

      {error && (
        <div className="mt-2 text-sm text-rose-600">
          {error}
        </div>
      )}
    </div>
  );
}